import type { SiteLang } from "@/hooks/useLanguage";

interface AuditI18n {
  seo: { title: string; description: string };
  breadcrumb: string;
  heroKicker: string;
  heroTitle: string;
  heroTagline: string;
  heroDesc: string;
  heroTrust: string;
  heroCta: string;
  whyKicker: string;
  whyTitle: string;
  whyIntro: string;
  whyBody: string[];
  whyCta: string;
  features: { title: string; desc: string }[];
  timelineKicker: string;
  timelineTitle: string;
  timelineSubtitle: string;
  steps: { label: string; desc: string }[];
  stepPrefix: string;
  formKicker: string;
  formTitle: string;
  formDesc: string;
  formNoObligation: string;
  trustTitle: string;
  trustItems: { title: string; desc: string }[];
  faqKicker: string;
  faqTitle: string;
  faqSubtitle: string;
  faqs: { q: string; a: string }[];
  mobileCta: string;
  loading: string;
  readyCta: string;
}

const en: AuditI18n = {
  seo: {
    title: "Request Your Audit – Global GxP, ISO & MDR Audit Services | QbD Group",
    description: "Request your independent audit for pharma, MedTech, or biotech. QbD Group's certified auditors perform ISO, GxP, and MDR audits worldwide. Response within 1 business day.",
  },
  breadcrumb: "Request Your Audit",
  heroKicker: "Audit services",
  heroTitle: "Request Your Audit Today",
  heroTagline: "Audit with Confidence. Anywhere, Anytime.",
  heroDesc: "Global GxP, MDR & IVDR audits executed by certified experts who know your industry and how to keep you inspection-ready.",
  heroTrust: "700+ life sciences specialists. 1,000+ audits delivered. Trusted by global Pharma and MedTech leaders.",
  heroCta: "Request your audit now",
  whyKicker: "Our expertise",
  whyTitle: "Why QbD Group?",
  whyIntro: "QbD Group delivers multidisciplinary audits for Pharma, Biotech, and MedTech.",
  whyBody: [
    "Keeping your operations compliant and audit-ready can be challenging, especially when juggling evolving regulations, supplier oversight, and maybe limited resources.",
    "An audit helps you identify gaps, strengthen your QMS, and build lasting improvements across your organization.",
    "QbD Group's certified auditors bring hands-on global experience across GxP, QMS (ISO), and regulatory frameworks. Whether you need support with a specific supplier/site assessment or your full annual audit plan, we combine compliance checks with actionable guidance to keep you inspection-ready.",
    "We go beyond the audit, helping you understand findings, address them effectively, and prevent future issues. The result? Fewer surprises, stronger systems, reduced risk, and faster time to market.",
  ],
  whyCta: "Request your audit now",
  features: [
    { title: "Certified global GxP auditors", desc: "ISO, GMP, GDP, GCP & more" },
    { title: "On-site or remote audits", desc: "Wherever you operate worldwide" },
    { title: "End-to-end support", desc: "From planning to CAPA follow-up" },
    { title: "Actionable insights, not just reports", desc: "Practical guidance you can act on" },
  ],
  timelineKicker: "The process",
  timelineTitle: "What to Expect",
  timelineSubtitle: "From request to audit — a simple, transparent process.",
  steps: [
    { label: "Submit your request", desc: "Fill in the form below" },
    { label: "We respond within 1 business day", desc: "No obligations attached" },
    { label: "Matched with the right auditor", desc: "Based on your industry & scope" },
    { label: "Audit scheduled & executed", desc: "On-site or remote, your choice" },
  ],
  stepPrefix: "Step",
  formKicker: "Get in touch",
  formTitle: "Tell us what you need and we'll match you with the right auditor.",
  formDesc: "Our international team of certified auditors is ready to support you. Share a few details about your audit request, and we'll match you with the right expert, wherever you operate.",
  formNoObligation: "No obligations. Response within 1 business day.",
  trustTitle: "Why work with us",
  trustItems: [
    { title: "1,000+ audits delivered", desc: "Trusted by global Pharma and MedTech leaders" },
    { title: "Response within 1 business day", desc: "No obligations, fast turnaround" },
    { title: "700+ life sciences specialists", desc: "Certified auditors across GxP, ISO, MDR & IVDR" },
  ],
  faqKicker: "FAQ",
  faqTitle: "Frequently Asked Questions",
  faqSubtitle: "Everything you need to know about our audit services.",
  faqs: [
    { q: "What types of audits does QbD Group perform?", a: "We perform a wide range of audits including GxP (GMP, GDP, GCP, GLP, GVP), ISO 13485, MDR & IVDR, QMS, supplier/vendor audits, mock inspections, and internal audits — across Pharma, Biotech, and MedTech." },
    { q: "Can audits be performed remotely?", a: "Yes. We offer both on-site and remote audit capabilities. Remote audits follow the same rigorous methodology and deliver equally actionable findings, suitable for supplier assessments and document-heavy reviews." },
    { q: "How quickly can an audit be scheduled?", a: "We respond within 1 business day. Depending on scope and auditor availability, most audits can be scheduled within 2–4 weeks of initial contact." },
    { q: "What industries do you serve?", a: "Our certified auditors have deep expertise in pharmaceuticals, biotechnology, medical devices, IVDs, combination products, and companion diagnostics." },
    { q: "Do you provide support after the audit?", a: "Absolutely. We go beyond the report — helping you understand findings, prioritize corrective actions, and implement CAPAs to build lasting compliance improvements." },
  ],
  mobileCta: "Request your audit now",
  loading: "Loading form…",
  readyCta: "Ready to get started?",
};

const es: AuditI18n = {
  seo: {
    title: "Solicita tu auditoría – Servicios de auditoría GxP, ISO y MDR | QbD Group",
    description: "Solicita tu auditoría independiente para pharma, MedTech o biotech. Los auditores certificados de QbD Group realizan auditorías ISO, GxP y MDR en todo el mundo. Respuesta en 1 día laborable.",
  },
  breadcrumb: "Solicita tu auditoría",
  heroKicker: "Servicios de auditoría",
  heroTitle: "Solicita tu auditoría hoy",
  heroTagline: "Auditorías con confianza. En cualquier lugar, en cualquier momento.",
  heroDesc: "Auditorías globales GxP, MDR e IVDR realizadas por expertos certificados que conocen tu industria y saben cómo mantenerte preparado para las inspecciones.",
  heroTrust: "Más de 700 especialistas en Life Sciences. Más de 1.000 auditorías realizadas. La confianza de líderes globales de Pharma y MedTech.",
  heroCta: "Solicita tu auditoría ahora",
  whyKicker: "Nuestra experiencia",
  whyTitle: "¿Por qué QbD Group?",
  whyIntro: "QbD Group ofrece auditorías multidisciplinares para Pharma, Biotech y MedTech.",
  whyBody: [
    "Mantener tus operaciones conformes y preparadas para auditorías puede ser un reto, especialmente cuando se gestionan regulaciones en evolución, supervisión de proveedores y recursos limitados.",
    "Una auditoría te ayuda a identificar brechas, fortalecer tu SGC y construir mejoras duraderas en toda tu organización.",
    "Los auditores certificados de QbD Group aportan experiencia global práctica en GxP, SGC (ISO) y marcos regulatorios. Ya necesites soporte para una evaluación específica de proveedor/planta o tu plan anual completo de auditorías, combinamos verificaciones de cumplimiento con orientación práctica para mantenerte preparado para las inspecciones.",
    "Vamos más allá de la auditoría, ayudándote a comprender los hallazgos, abordarlos eficazmente y prevenir problemas futuros. ¿El resultado? Menos sorpresas, sistemas más sólidos, menor riesgo y un time to market más rápido.",
  ],
  whyCta: "Solicita tu auditoría ahora",
  features: [
    { title: "Auditores GxP certificados a nivel global", desc: "ISO, GMP, GDP, GCP y más" },
    { title: "Auditorías presenciales o remotas", desc: "Dondequiera que operes en el mundo" },
    { title: "Soporte integral", desc: "Desde la planificación hasta el seguimiento de CAPAs" },
    { title: "Insights accionables, no solo informes", desc: "Orientación práctica para actuar" },
  ],
  timelineKicker: "El proceso",
  timelineTitle: "Qué esperar",
  timelineSubtitle: "De la solicitud a la auditoría — un proceso sencillo y transparente.",
  steps: [
    { label: "Envía tu solicitud", desc: "Rellena el formulario" },
    { label: "Respondemos en 1 día laborable", desc: "Sin compromisos" },
    { label: "Te asignamos el auditor adecuado", desc: "Según tu industria y alcance" },
    { label: "Auditoría programada y ejecutada", desc: "Presencial o remota, tú eliges" },
  ],
  stepPrefix: "Paso",
  formKicker: "Contacto",
  formTitle: "Cuéntanos lo que necesitas y te asignaremos el auditor adecuado.",
  formDesc: "Nuestro equipo internacional de auditores certificados está preparado para ayudarte. Comparte algunos detalles sobre tu solicitud de auditoría y te asignaremos el experto adecuado, dondequiera que operes.",
  formNoObligation: "Sin compromisos. Respuesta en 1 día laborable.",
  trustTitle: "Por qué trabajar con nosotros",
  trustItems: [
    { title: "Más de 1.000 auditorías realizadas", desc: "La confianza de líderes globales de Pharma y MedTech" },
    { title: "Respuesta en 1 día laborable", desc: "Sin compromisos, respuesta rápida" },
    { title: "Más de 700 especialistas en Life Sciences", desc: "Auditores certificados en GxP, ISO, MDR e IVDR" },
  ],
  faqKicker: "FAQ",
  faqTitle: "Preguntas frecuentes",
  faqSubtitle: "Todo lo que necesitas saber sobre nuestros servicios de auditoría.",
  faqs: [
    { q: "¿Qué tipos de auditorías realiza QbD Group?", a: "Realizamos una amplia gama de auditorías, incluyendo GxP (GMP, GDP, GCP, GLP, GVP), ISO 13485, MDR e IVDR, SGC, auditorías de proveedores, simulacros de inspección y auditorías internas, en los sectores Pharma, Biotech y MedTech." },
    { q: "¿Se pueden realizar auditorías de forma remota?", a: "Sí. Ofrecemos tanto auditorías presenciales como remotas. Las auditorías remotas siguen la misma metodología rigurosa y proporcionan hallazgos igualmente accionables, ideales para evaluaciones de proveedores y revisiones documentales." },
    { q: "¿Con qué rapidez se puede programar una auditoría?", a: "Respondemos en 1 día laborable. Dependiendo del alcance y la disponibilidad del auditor, la mayoría de las auditorías pueden programarse en 2–4 semanas desde el contacto inicial." },
    { q: "¿Qué industrias atendéis?", a: "Nuestros auditores certificados tienen amplia experiencia en farmacéutica, biotecnología, dispositivos médicos, IVD, productos combinados y diagnósticos complementarios." },
    { q: "¿Ofrecéis soporte después de la auditoría?", a: "Por supuesto. Vamos más allá del informe, ayudándote a comprender los hallazgos, priorizar las acciones correctivas e implementar CAPAs para construir mejoras de cumplimiento duraderas." },
  ],
  mobileCta: "Solicita tu auditoría ahora",
  loading: "Cargando formulario…",
  readyCta: "¿Listo para empezar?",
};

const zh: AuditI18n = {
  seo: {
    title: "申请您的审计 – 全球 GxP、ISO 与 MDR 审计服务 | QbD Group",
    description: "为制药、MedTech 或生物技术申请独立审计。QbD Group 的认证审计员在全球开展 ISO、GxP 和 MDR 审计,1 个工作日内回复。",
  },
  breadcrumb: "申请您的审计",
  heroKicker: "审计服务",
  heroTitle: "立即申请您的审计",
  heroTagline: "自信开展审计。随时随地。",
  heroDesc: "由认证专家执行的全球 GxP、MDR 与 IVDR 审计,他们了解您的行业,助您始终为检查做好准备。",
  heroTrust: "700 多名 Life Sciences 专家。已交付 1,000 多次审计。深受全球制药与 MedTech 领军企业信赖。",
  heroCta: "立即申请审计",
  whyKicker: "我们的专长",
  whyTitle: "为何选择 QbD Group?",
  whyIntro: "QbD Group 为制药、生物技术与 MedTech 提供多学科审计。",
  whyBody: [
    "在不断演变的法规、供应商监督以及可能有限的资源之间周旋时,保持运营合规并随时迎接审计可能颇具挑战。",
    "审计帮助您识别差距、强化质量管理体系,并在整个组织内构建持久的改进。",
    "QbD Group 的认证审计员在 GxP、QMS(ISO)与法规框架方面拥有亲身的全球经验。无论您需要针对特定供应商/场地评估的支持,还是完整的年度审计计划,我们都将合规检查与可执行的指导相结合,助您始终为检查做好准备。",
    "我们超越审计本身,帮助您理解发现项、有效解决并预防未来问题。结果如何?更少的意外、更稳健的体系、更低的风险,以及更快的上市时间。",
  ],
  whyCta: "立即申请审计",
  features: [
    { title: "全球认证 GxP 审计员", desc: "ISO、GMP、GDP、GCP 等" },
    { title: "现场或远程审计", desc: "覆盖您在全球的任何运营地" },
    { title: "端到端支持", desc: "从计划到 CAPA 跟进" },
    { title: "可执行的洞察,而不只是报告", desc: "切实可行的实用指导" },
  ],
  timelineKicker: "流程",
  timelineTitle: "您可以期待什么",
  timelineSubtitle: "从申请到审计 — 简单、透明的流程。",
  steps: [
    { label: "提交您的申请", desc: "填写下方表单" },
    { label: "我们将在 1 个工作日内回复", desc: "无任何义务" },
    { label: "为您匹配合适的审计员", desc: "基于您的行业与范围" },
    { label: "审计安排并执行", desc: "现场或远程,由您选择" },
  ],
  stepPrefix: "步骤",
  formKicker: "联系我们",
  formTitle: "告诉我们您的需求,我们将为您匹配合适的审计员。",
  formDesc: "我们的国际认证审计员团队随时为您提供支持。请分享您审计申请的几项细节,我们将在您所在的任何地区为您匹配合适的专家。",
  formNoObligation: "无任何义务。1 个工作日内回复。",
  trustTitle: "为何与我们合作",
  trustItems: [
    { title: "已交付 1,000 多次审计", desc: "深受全球制药与 MedTech 领军企业信赖" },
    { title: "1 个工作日内回复", desc: "无义务,响应迅速" },
    { title: "700 多名 Life Sciences 专家", desc: "覆盖 GxP、ISO、MDR 与 IVDR 的认证审计员" },
  ],
  faqKicker: "常见问题",
  faqTitle: "常见问题解答",
  faqSubtitle: "您需要了解的关于我们审计服务的一切。",
  faqs: [
    { q: "QbD Group 开展哪些类型的审计?", a: "我们开展广泛的审计,包括 GxP(GMP、GDP、GCP、GLP、GVP)、ISO 13485、MDR 与 IVDR、QMS、供应商/外协方审计、模拟检查以及内部审计 — 覆盖制药、生物技术与 MedTech。" },
    { q: "可以远程开展审计吗?", a: "可以。我们同时提供现场和远程审计能力。远程审计遵循同样严谨的方法学,并产出同样可执行的发现项,适用于供应商评估和文档密集型审查。" },
    { q: "审计可以多快安排?", a: "我们将在 1 个工作日内回复。视范围与审计员的可用情况,大多数审计可在初次联系后的 2–4 周内安排。" },
    { q: "你们服务于哪些行业?", a: "我们的认证审计员在制药、生物技术、医疗器械、IVD、组合产品和伴随诊断等领域拥有深厚专长。" },
    { q: "审计后你们是否提供支持?", a: "当然。我们超越报告本身 — 帮助您理解发现项、优先安排纠正措施,并实施 CAPA 以构建持久的合规改进。" },
  ],
  mobileCta: "立即申请审计",
  loading: "正在加载表单……",
  readyCta: "准备好开始了吗?",
};

const translations: Record<SiteLang, AuditI18n> = { en, es, "zh-cn": zh };

export const getAuditT = (lang: SiteLang) => translations[lang] ?? en;
